'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import type { Product } from '@/lib/shopify/types';
import { formatPrice } from '@/lib/shopify';
import { useWishlist } from '@/context/wishlist-context';

interface ProductCardProps {
  product: Product;
}

// Map color names to hex values
const colorMap: Record<string, string> = {
  black: '#000000',
  white: '#FFFFFF',
  red: '#DC2626',
  blue: '#2563EB',
  navy: '#1E3A5F',
  green: '#16A34A',
  yellow: '#EAB308',
  orange: '#EA580C',
  purple: '#9333EA',
  pink: '#EC4899',
  gray: '#6B7280',
  grey: '#6B7280',
  brown: '#92400E',
  beige: '#D4C4A8',
  cream: '#FFFDD0',
  maroon: '#800000',
  burgundy: '#800020',
  olive: '#808000',
  teal: '#0D9488',
  charcoal: '#36454F',
  khaki: '#C3B091',
};

function getColorHex(colorName: string): string {
  const lowerColor = colorName.toLowerCase();

  // Check for exact match
  if (colorMap[lowerColor]) {
    return colorMap[lowerColor];
  }

  // Check if color name contains any known color
  for (const [name, hex] of Object.entries(colorMap)) {
    if (lowerColor.includes(name)) {
      return hex;
    }
  }

  // Default to gray
  return '#6B7280';
}

export default function ProductCard({ product }: ProductCardProps) {
  const { isInWishlist, toggleItem } = useWishlist();
  const [isAnimating, setIsAnimating] = useState(false);

  const inWishlist = isInWishlist(product.id);

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAnimating(true);
    toggleItem(product);
    setTimeout(() => setIsAnimating(false), 300);
  };

  // Extract color options from product
  const colorOption = product.options.find(
    (opt) => opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour'
  );
  const colors = colorOption?.values || [];
  const displayColors = colors.slice(0, 3);
  const remainingColors = colors.length - 3;

  const price = formatPrice(product.priceRange.minVariantPrice);
  const compareAtPrice = product.variants[0]?.compareAtPrice
    ? formatPrice(product.variants[0].compareAtPrice)
    : null;

  return (
    <div className="group">
      {/* Image Container */}
      <Link
        href={`/products/${product.handle}`}
        className="block relative aspect-[3/4] bg-[var(--color-off-white)] overflow-hidden mb-3"
      >
        {product.featuredImage ? (
          <Image
            src={product.featuredImage.url}
            alt={product.featuredImage.altText || product.title}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
            No image
          </div>
        )}

        {/* Wishlist Button */}
        <button
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-md transition-all hover:scale-110"
          onClick={handleWishlistClick}
          aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          style={{
            transform: isAnimating ? 'scale(1.2)' : 'scale(1)',
            transition: 'transform 0.2s ease'
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={inWishlist ? '#e11d48' : 'none'}
            stroke={inWishlist ? '#e11d48' : 'currentColor'}
            strokeWidth={inWishlist ? 0 : 1.5}
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
            />
          </svg>
        </button>

        {/* Sale Badge */}
        {compareAtPrice && (
          <div className="absolute top-3 left-3 bg-brand-red text-white text-xs font-medium px-2 py-1">
            SALE
          </div>
        )}
      </Link>

      {/* Product Info */}
      <div className="space-y-1">
        {/* Title */}
        <Link href={`/products/${product.handle}`}>
          <h3 className="text-sm font-medium text-[var(--color-text-primary)] line-clamp-1 hover:underline">
            {product.title}
          </h3>
        </Link>

        {/* Price */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">
            {price}
          </span>
          {compareAtPrice && (
            <span className="text-sm text-gray-400 line-through">{compareAtPrice}</span>
          )}
        </div>

        {/* Color Swatches */}
        {colors.length > 0 && (
          <div className="flex items-center gap-1 pt-1">
            {displayColors.map((color) => (
              <span
                key={color}
                className="w-4 h-4 rounded-full border border-gray-300"
                style={{ backgroundColor: getColorHex(color) }}
                title={color}
              />
            ))}
            {remainingColors > 0 && (
              <span className="text-xs text-gray-500 ml-1">+{remainingColors}</span>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
